import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { supabase, BRAND, INK, TEXT, MUTED, LINE, ROW_LINE, SURFACE, text, card, btn, input, linkBtn, errorText, savedText, segWrap, segBtn, chip, fetchAll, WHITE, HOVER_BG } from "./constants";
import { ConfirmBar } from "./shared";
import { useIsMobile } from "./lib/responsive";

// ─── RANGO SALARIAL DE LA POSICIÓN (15/10) ───────────────────────────────────
// Va arriba de la pestaña Mapping.
//   · Admin: "Referencia de mercado 2026" (tabla public.salary_benchmarks,
//     solo admin por RLS): comparativa del puesto en todos los sectores y
//     tamaños de empresa, con el más parecido a la posición señalado, para
//     orientar. Debajo, el formulario "Rango recomendado" que publica en
//     positions.salary_reco.
//   · Cliente: SOLO positions.salary_reco, si existe. Nunca la referencia, y
//     en ninguna pantalla se nombra de dónde salen los datos.
// Las filas de la referencia se piden solo cuando el admin busca, nunca al
// abrir la posición.
//   · Admin, debajo (27/10): "Ofertas y datos de mercado", de
//     public.salary_observations (solo admin por RLS; la alimenta Borja por
//     SQL). Mismos nombres del puesto que la referencia. Nunca se pide ni se
//     pinta `source` (ni `company`, `url` o `note`, que la delatarían):
//     misma regla que la referencia, en pantalla no se nombra la fuente.

const YEAR = 2026;
const num = (v) => { const n = parseFloat(String(v).replace(",", ".")); return Number.isFinite(n) ? n : null; };
const fmtK = (v) => (Number.isInteger(v) ? v : Math.round(v * 10) / 10);
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// "90-110k + bonus", "90.000 – 110.000 €", "100k" → cifras en K
const readClientRange = (s) => {
  const nums = (String(s || "").replace(/\./g, "").match(/\d+(?:,\d+)?/g) || [])
    .map(x => parseFloat(x.replace(",", ".")))
    .map(x => (x >= 1000 ? x / 1000 : x))
    .filter(x => x >= 10 && x <= 2000);
  return nums.length ? { min: Math.min(...nums), max: Math.max(...nums) } : null;
};

// Nombres equivalentes de los puestos más habituales: el título de la posición
// suele estar en inglés y la referencia en español (o al revés)
const SYNONYMS = [
  [/\b(cfo|chief financial|director financiero|directora financiera|director de finanzas|finance director)\b/, ["Director Financiero", "Chief Financial Officer", "CFO", "Director de Finanzas"]],
  [/\b(ceo|chief executive|director general|consejero delegado|managing director)\b/, ["Director General", "Chief Executive Officer", "CEO", "Consejero Delegado"]],
  [/\b(cto|chief technology|director de tecnologia|director tecnico|head of engineering)\b/, ["Director de Tecnología", "Chief Technology Officer", "CTO", "Director Técnico"]],
  [/\b(coo|chief operating|director de operaciones)\b/, ["Director de Operaciones", "Chief Operating Officer", "COO"]],
  [/\b(cmo|chief marketing|director de marketing)\b/, ["Director de Marketing", "Chief Marketing Officer", "CMO"]],
  [/\b(chro|cpo|chief people|chief human|recursos humanos|director de personas|head of people)\b/, ["Director de Recursos Humanos", "Director de Personas", "CHRO"]],
  [/\b(cco|chief commercial|chief revenue|director comercial|director de ventas|head of sales)\b/, ["Director Comercial", "Director de Ventas", "Chief Commercial Officer"]],
  [/\b(cio|chief information|director de sistemas)\b/, ["Director de Sistemas", "Chief Information Officer", "CIO"]],
  [/\b(controller)\b/, ["Controller", "Financial Controller"]],
];
const initialTerms = (title) => {
  const t = norm(title);
  const hit = SYNONYMS.find(([re]) => re.test(t));
  const list = hit ? [title, ...hit[1]] : [title];
  return [...new Set(list.filter(Boolean).map(x => x.trim()))].join(", ");
};

// Nombres separados por comas → consulta a la referencia. Sin puntos ni
// paréntesis (rompen el filtro "or"); cada nombre entre comillas para que
// "Director Financiero" se lea entero.
const termsOf = (q) => String(q || "").split(",").map(x => x.replace(/[().]/g, " ").replace(/"/g, "").trim()).filter(x => x.length >= 2);
const fetchBenchmarks = (terms) => supabase.from("salary_benchmarks").select("*")
  .eq("year", YEAR).or(terms.map(x => `role.ilike."%${x}%"`).join(",")).limit(3000);

// Ofertas y datos sueltos. Columnas acotadas a propósito: sin source, company,
// url ni note. Paginado (pueden pasar de 1.000) con orden fijo.
const OBS_COLS = "id, observed_on, source_kind, company_type, sector, role, area, seniority, years_min, years_max, city, country, work_mode, currency, min_k, max_k, pay_basis, bonus, equity";
const fetchObservations = (terms, allCurrencies) => fetchAll(() => {
  let qb = supabase.from("salary_observations").select(OBS_COLS)
    .or(terms.map(x => `role.ilike."%${x}%"`).join(","));
  if (!allCurrencies) qb = qb.eq("currency", "EUR");
  return qb.order("observed_on", { ascending: false, nullsFirst: false }).order("id");
});
const CUR_SYMBOL = { EUR: "€", GBP: "£", USD: "$" };
// Tipo de origen: se traduce la etiqueta; un valor desconocido sale tal cual
const SOURCE_KIND = {
  offer: ["Oferta", "Job ad"], job_ad: ["Oferta", "Job ad"], oferta: ["Oferta", "Job ad"],
  headhunter: ["Headhunter", "Headhunter"],
  placement: ["Colocación", "Placement"], colocacion: ["Colocación", "Placement"],
  survey: ["Encuesta", "Survey"], report: ["Informe", "Report"], candidate: ["Candidato", "Candidate"],
};
const WORK_MODE = { remote: ["Remoto", "Remote"], remoto: ["Remoto", "Remote"], hybrid: ["Híbrido", "Hybrid"], hibrido: ["Híbrido", "Hybrid"], onsite: ["Presencial", "On-site"], office: ["Presencial", "On-site"], presencial: ["Presencial", "On-site"] };
const YEAR_MS = 365 * 24 * 3600 * 1000;

// Marcas redondas para la escala (50, 75, 100…)
const niceStep = (span) => [5, 10, 20, 25, 50, 100, 200].find(x => span / x <= 6) || 250;

export const recoText = (r) => {
  if (!r || r.min_k == null) return "";
  const range = r.max_k != null && r.max_k !== r.min_k ? `${fmtK(r.min_k)}–${fmtK(r.max_k)}K` : `${fmtK(r.min_k)}K`;
  // Sin "+" sueltos al principio o al final ("equity + 15–20 % +")
  const b = (r.bonus || "").trim().replace(/^\+\s*|\s*\+$/g, "").trim();
  return b ? `${range} + ${b}${/bonus|variable/i.test(b) ? "" : " bonus"}` : range;
};

const SalaryReco = ({ position, lang, isAdmin, setOpenPositionsList = () => {} }) => {
  const es = lang === "es";
  const T = (a, b) => (es ? a : b);
  const isMobile = useIsMobile();
  const reco = position.salaryReco || null;

  // Admin: búsqueda, comparativa y formulario
  const [q, setQ] = useState(() => initialTerms(position.title || ""));
  const [rows, setRows] = useState(null);         // filas de salary_benchmarks
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState("");
  const [chosen, setChosen] = useState(reco?.closest_key || null); // fila marcada a mano como la más parecida
  const [level, setLevel] = useState(reco?.level || null);        // nivel del puesto (null = todos)
  const [open, setOpen] = useState(null);          // fila desplegada (detalle por bandas)
  const [form, setForm] = useState({ min: reco?.min_k ?? "", max: reco?.max_k ?? "", bonus: reco?.bonus || "", perks: reco?.perks || "", note: reco?.note || "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [askWithdraw, setAskWithdraw] = useState(false);
  const [expanded, setExpanded] = useState(!reco);    // tarjeta abierta: sí si aún no hay nada publicado
  const [obs, setObs] = useState(null);           // filas de salary_observations
  const [obsErr, setObsErr] = useState("");
  const [otherCur, setOtherCur] = useState(false);  // "Incluir otras divisas"
  const [obsTerms, setObsTerms] = useState(() => termsOf(initialTerms(position.title || "")));

  // Ofertas y datos: mismos nombres que la última búsqueda; se repiten al
  // buscar y al cambiar el interruptor de divisas
  useEffect(() => {
    if (!isAdmin || !obsTerms.length) return undefined;
    let alive = true;
    setObsErr("");
    fetchObservations(obsTerms, otherCur)
      .then(data => { if (alive) setObs(data); })
      .catch(e => { if (alive) { setObsErr(e.message || String(e)); setObs([]); } });
    return () => { alive = false; };
  }, [isAdmin, obsTerms, otherCur]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const terms = termsOf(initialTerms(position.title || ""));
    if (!terms.length) return undefined;
    let alive = true;
    setSearching(true);
    fetchBenchmarks(terms).then(({ data, error }) => {
      if (!alive) return;
      setSearching(false);
      if (error) { setErr(error.message); setRows([]); } else setRows(data || []);
    });
    return () => { alive = false; };
  }, [isAdmin, position.id, position.title]);

  const pad = isMobile ? 16 : 22;
  const box = { ...card, padding: pad };

  // ── Lo que ve el cliente ──
  const clientCard = reco && (
    <div style={box}>
      <div style={text.label}>{T("Rango recomendado", "Recommended range")}</div>
      <div style={{ fontSize: isMobile ? 22 : 22, fontWeight: 800, color: INK, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>{recoText(reco)}</div>
      {reco.perks && <div style={{ ...text.body, color: INK, marginTop: 6 }}><span style={{ color: MUTED }}>{T("Equity y otros: ", "Equity & other: ")}</span>{reco.perks}</div>}
      {reco.note && <div style={{ ...text.body, marginTop: 8, whiteSpace: "pre-wrap" }}>{reco.note}</div>}
    </div>
  );
  if (!isAdmin) return clientCard || null;

  // ── Admin: búsqueda por varios nombres a la vez ──
  const search = async () => {
    const terms = termsOf(q);
    if (!terms.length) return;
    setSearching(true); setErr(""); setOpen(null);
    setObsTerms(terms);
    const { data, error } = await fetchBenchmarks(terms);
    setSearching(false);
    if (error) { setErr(error.message); setRows([]); return; }
    setRows(data || []);
  };

  // Una "referencia" = puesto · área · sector · grupo · tamaño, con sus bandas
  const keyOf = (r) => [r.role, r.area, r.sector, r.grp, r.segment].map(x => x ?? "").join("|");
  const refs = [];
  (rows || []).forEach(r => {
    const k = keyOf(r);
    let g = refs.find(x => x.key === k);
    if (!g) { g = { key: k, role: r.role, area: r.area, sector: r.sector, grp: r.grp, segment: r.segment, bands: [] }; refs.push(g); }
    g.bands.push(r);
  });
  // Niveles que aparecen (0-5 años, 6-10 años, +11 años, Senior…), en su orden
  const levels = [];
  (rows || []).slice().sort((a, b) => (a.band_order ?? 0) - (b.band_order ?? 0))
    .forEach(r => { if (r.band && !levels.includes(r.band)) levels.push(r.band); });
  const lvl = level && levels.includes(level) ? level : null;
  refs.forEach(g => {
    g.bands.sort((a, b) => (a.band_order ?? 0) - (b.band_order ?? 0));
    // Con un nivel elegido, cada referencia enseña solo ese nivel; si no lo
    // tiene, enseña todos los suyos y se atenúa ("sin este nivel")
    const inLevel = lvl ? g.bands.filter(b => b.band === lvl) : [];
    g.hasLevel = !lvl || inLevel.length > 0;
    const use = lvl && inLevel.length ? inLevel : g.bands;
    g.shown = use;
    const mins = use.map(b => b.min_k).filter(v => v != null);
    const maxs = use.map(b => (b.max_k != null ? b.max_k : b.min_k)).filter(v => v != null);
    g.lo = mins.length ? Math.min(...mins) : null;
    g.hi = maxs.length ? Math.max(...maxs) : null;
    g.open = use.some(b => b.max_k == null);
    g.mid = g.lo != null && g.hi != null ? (g.lo + g.hi) / 2 : null;
  });
  const list = refs.filter(g => g.lo != null).sort((a, b) => a.mid - b.mid);

  // Lo que pide el cliente y lo que vas a recomendar, en K
  const asked = position.formData?.salaryRange || "";
  const askedK = readClientRange(asked);
  const recoK = num(form.min) != null ? { min: num(form.min), max: num(form.max) ?? num(form.min) } : null;

  // Más parecido a la posición: cuántas palabras del área, sector, grupo y
  // tamaño de cada referencia aparecen en el título y la descripción del
  // puesto. A igualdad, la más cercana a lo que pide el cliente. Es una
  // sugerencia: pulsando otra fila se cambia.
  const ctx = norm([position.title, position.jobDescription, ...Object.values(position.formData || {})].join(" "));
  const STOP = new Set(["para", "otros", "otras", "resto", "general", "sector", "empresa", "empresas", "servicios", "grupo", "todos", "todas"]);
  const scoreOf = (g) => {
    const words = norm([g.area, g.sector, g.grp, g.segment].filter(Boolean).join(" ")).split(/[^a-z0-9]+/).filter(w => w.length >= 4 && !STOP.has(w));
    return [...new Set(words)].filter(w => ctx.includes(w)).length;
  };
  const dist = (g) => (askedK ? Math.abs(g.mid - (askedK.min + askedK.max) / 2) : 0);
  let suggested = null;
  list.forEach(g => {
    g.score = scoreOf(g);
    if (!suggested || g.score > suggested.score || (g.score === suggested.score && dist(g) < dist(suggested))) suggested = g;
  });
  const markedKey = chosen && list.some(g => g.key === chosen) ? chosen : suggested?.key || null;
  const marked = list.find(g => g.key === markedKey) || null;
  const markedWhy = chosen && markedKey === chosen ? T("Marcado por ti", "Marked by you")
    : suggested && suggested.score > 0 ? T("Sugerido: coincide con el sector o el tamaño del puesto", "Suggested: matches the role's sector or size")
    : T("Sugerido: el más cercano a lo que pide el cliente", "Suggested: closest to what the client asks");

  // Escala común de las barras
  const allVals = [...list.flatMap(g => [g.lo, g.hi]), askedK?.min, askedK?.max, recoK?.min, recoK?.max].filter(v => v != null);
  const sMin = allVals.length ? Math.max(0, Math.min(...allVals) * 0.9) : 0;
  const sMax = allVals.length ? Math.max(...allVals) * 1.05 : 1;
  const step = niceStep(sMax - sMin);
  const ticks = [];
  for (let v = Math.ceil(sMin / step) * step; v <= sMax; v += step) ticks.push(v);
  const pos = (v) => `${((v - sMin) / (sMax - sMin || 1)) * 100}%`;
  const span = (a, b) => ({ left: pos(a), width: `calc(${pos(b)} - ${pos(a)})` });

  // Dónde cae lo que pide el cliente frente a todas las referencias
  const askedMid = askedK ? (askedK.min + askedK.max) / 2 : null;
  const belowCount = askedMid != null ? list.filter(g => g.mid < askedMid).length : 0;
  const rangeTxt = (lo, hi, isOpen) => (isOpen && hi === lo ? `> ${fmtK(lo)}K` : `${fmtK(lo)}–${fmtK(hi)}K${isOpen ? "+" : ""}`);
  const bandTxt = (b) => (b.max_k == null ? `> ${fmtK(b.min_k)}K` : `${fmtK(b.min_k)}–${fmtK(b.max_k)}K`);
  const where = (g) => [g.area, g.sector, g.grp].filter(Boolean).join(" · ");
  const sizeTxt = (g) => g.segment || T("Todos los tamaños", "All company sizes");

  const publish = async (value) => {
    setSaving(true); setErr(""); setSaved(false);
    let payload = null;
    if (value !== null) {
      const { data: { user } } = await supabase.auth.getUser();
      payload = {
        min_k: num(form.min), max_k: num(form.max), bonus: form.bonus.trim() || null, perks: form.perks.trim() || null, note: form.note.trim() || null,
        closest_key: chosen || null, level: lvl,
        published_at: new Date().toISOString(), published_by: user?.email || null,
      };
    }
    // RLS bloqueado = 200 con 0 filas: se comprueba que devuelve la fila
    const { data, error } = await supabase.from("positions").update({ salary_reco: payload }).eq("id", position.id).select("id, salary_reco");
    setSaving(false);
    if (error || !data || data.length === 0) { setErr(T("No se pudo guardar. ", "Couldn't save. ") + (error?.message || "")); return; }
    setOpenPositionsList(prev => prev.map(p => p.id === position.id ? { ...p, salaryReco: data[0].salary_reco } : p));
    setSaved(true); setAskWithdraw(false);
    setTimeout(() => setSaved(false), 2500);
  };
  const canPublish = num(form.min) !== null && !saving;
  const field = (k, label, ph, extra) => (
    <label style={{ display: "block", minWidth: 0, ...extra }}>
      <span style={{ ...text.label, display: "block", marginBottom: 6 }}>{label}</span>
      <input value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} placeholder={ph}
        style={{ ...input, ...(isMobile ? { height: 44, fontSize: 16 } : null) }} />
    </label>
  );

  // Capas comunes de la columna del gráfico: lo que pide el cliente (gris) y tu
  // rango (azul claro con bordes discontinuos), de arriba abajo en todas las filas
  const layers = (
    <>
      {askedK && <div style={{ position: "absolute", top: 0, bottom: 0, ...span(askedK.min, askedK.max), background: LINE }} />}
      {recoK && <div style={{ position: "absolute", top: 0, bottom: 0, ...span(recoK.min, recoK.max), background: HOVER_BG, borderLeft: `1.5px dashed ${BRAND}`, borderRight: `1.5px dashed ${BRAND}`, boxSizing: "border-box", minWidth: 3 }} />}
    </>
  );
  const bar = (g, on) => (
    <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", height: 8, borderRadius: 4, ...span(g.lo, g.hi), background: on ? BRAND : TEXT, minWidth: 4 }} />
  );
  const cols = "minmax(0, 1fr) minmax(0, 1.5fr) 104px";
  const levelsTxt = (g) => {
    if (!g.hasLevel) return T("sin este nivel", "no such level");
    if (lvl) return g.shown[0]?.bonus || lvl;
    const n = g.bands.length;
    const bonus = n === 1 ? g.bands[0].bonus : null;
    return [bonus, n === 1 ? T("1 nivel", "1 level") : T(`${n} niveles`, `${n} levels`)].filter(Boolean).join(" · ");
  };

  // Resumen de una línea para la tarjeta cerrada
  const summary = [
    reco ? `${T("Publicado", "Published")}: ${recoText(reco)}` : T("Sin publicar", "Not published"),
    askedK && `${T("cliente pide", "client asks")} ${rangeTxt(askedK.min, askedK.max, false)}`,
    marked && `${T("más parecido", "closest")} ${rangeTxt(marked.lo, marked.hi, marked.open)}`,
  ].filter(Boolean).join(" · ");
  const sectionHead = (title, right) => (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
      <span style={{ ...text.label }}>{title}</span>{right}
    </div>
  );

  // Una sola tarjeta (solo admin). Cerrada si ya hay rango publicado: el
  // resumen dice lo esencial y el Mapping queda a la vista. Abierta si no.
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div role="button" tabIndex={0} onClick={() => setExpanded(v => !v)} onKeyDown={e => { if (e.key === "Enter") setExpanded(v => !v); }}
        aria-expanded={expanded}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: `${isMobile ? 14 : 16}px ${pad}px`, cursor: "pointer", minHeight: isMobile ? 44 : undefined }}>
        <ChevronDown size={18} strokeWidth={1.75} color={MUTED} style={{ transform: expanded ? "none" : "rotate(-90deg)", transition: "transform 0.15s", flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={text.cardTitle}>{T("Rango salarial", "Salary range")}</span>
            <span style={chip(false)}>{reco ? T("Publicado", "Published") : T("Sin publicar", "Not published")}</span>
          </div>
          {!expanded && <div style={{ ...text.cardSub, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "normal" : "nowrap", fontVariantNumeric: "tabular-nums" }}>{summary}</div>}
        </div>
        {!isMobile && <span style={{ ...text.cardSub, flexShrink: 0 }}>{T("Solo admin", "Admin only")}</span>}
      </div>

      {expanded && <>
      <div style={{ padding: `${isMobile ? 14 : 18}px ${pad}px ${pad}px`, borderTop: `1px solid ${ROW_LINE}` }}>
        {sectionHead(T("Referencia de mercado 2026", "Market reference 2026"), <span style={text.cardSub}>{T("El cliente no ve esto", "The client never sees this")}</span>)}

        {/* Buscador: varios nombres del puesto separados por comas */}
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === "Enter") search(); }}
            placeholder={T("Nombres del puesto, separados por comas", "Role names, comma-separated")}
            style={{ ...input, flex: 1, ...(isMobile ? { height: 44, fontSize: 16, flex: "1 1 100%" } : null) }} />
          <button onClick={search} disabled={searching || !q.trim()}
            style={{ ...(searching || !q.trim() ? btn.disabled : btn.primary), ...(isMobile ? { minHeight: 44, flex: 1 } : null) }}>
            {searching ? T("Buscando...", "Searching...") : T("Actualizar", "Update")}
          </button>
        </div>
        <div style={{ ...text.cardSub, marginTop: 6 }}>{T("Fijo bruto anual, miles de €. Puedes añadir o quitar nombres del puesto.", "Gross annual base, € thousands. You can add or remove role names.")}</div>
        {err && <div style={{ ...errorText, marginTop: 8 }}>{err}</div>}

        {rows && list.length === 0 && (
          <div style={{ ...text.body, color: MUTED, marginTop: 12 }}>{T("Sin resultados. Prueba con otros nombres del puesto (p. ej. «Director Financiero»).", "No results. Try other names for the role (e.g. «Director Financiero»).")}</div>
        )}

        {searching && !rows && <div style={{ ...text.body, color: MUTED, marginTop: 12 }}>{T("Cargando la comparativa…", "Loading the comparison…")}</div>}

        {list.length > 0 && (
          <div style={{ marginTop: 14 }}>
            {/* Resumen en una línea por idea */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
              {marked && (
                <div style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: "10px 12px", boxShadow: `inset 3px 0 0 ${BRAND}` }}>
                  <div style={text.label}>{T("Más parecido al puesto", "Closest to the role")}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: INK, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{rangeTxt(marked.lo, marked.hi, marked.open)}</div>
                  <div style={{ ...text.bodySm, color: INK, marginTop: 2 }}>{where(marked)} · {sizeTxt(marked)}</div>
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{markedWhy}</div>
                </div>
              )}
              <div style={{ background: SURFACE, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={text.label}>{T("Lo que pide el cliente", "What the client asks")}</div>
                {askedK ? (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 800, color: INK, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{rangeTxt(askedK.min, askedK.max, false)}</div>
                    <div style={{ ...text.bodySm, color: INK, marginTop: 2 }}>
                      {T(`Por encima de ${belowCount} de ${list.length} referencias`, `Above ${belowCount} of ${list.length} references`)}
                      {marked && (askedK.max < marked.lo ? T(" · por debajo de la más parecida", " · below the closest one") : askedK.min > marked.hi ? T(" · por encima de la más parecida", " · above the closest one") : T(" · dentro de la más parecida", " · within the closest one"))}
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{asked}</div>
                  </>
                ) : <div style={{ ...text.body, color: MUTED, marginTop: 4 }}>{asked || T("Sin rango en el brief", "No range in the brief")}</div>}
              </div>
            </div>

            {/* Nivel del puesto: cada referencia enseña solo ese nivel */}
            {levels.length > 1 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ ...text.label, marginBottom: 6 }}>{T("Nivel del puesto", "Role level")}</div>
                <div style={{ ...segWrap, width: isMobile ? "100%" : "fit-content", overflowX: "auto", boxSizing: "border-box" }}>
                  {[null, ...levels].map(l => (
                    <button key={l || "all"} onClick={() => setLevel(l)} style={{ ...segBtn(lvl === l), ...(isMobile ? { minHeight: 40, flex: "1 0 auto" } : null) }}>
                      {l || T("Todos", "All")}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Escala y etiquetas de las columnas */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : cols, gap: 12, alignItems: "end", marginTop: 16, padding: "0 10px" }}>
              {!isMobile && <div style={text.label}>{T(`${list.length} referencias, de menor a mayor`, `${list.length} references, low to high`)}</div>}
              <div style={{ position: "relative", height: 34 }}>
                {askedK && <div style={{ position: "absolute", top: 0, ...span(askedK.min, askedK.max), textAlign: "center", fontSize: 11, fontWeight: 700, color: TEXT, whiteSpace: "nowrap", overflow: "visible" }}>{T("Cliente pide", "Client asks")}</div>}
                {recoK && <div style={{ position: "absolute", top: askedK ? 11 : 0, ...span(recoK.min, recoK.max), textAlign: "center", fontSize: 11, fontWeight: 700, color: INK, whiteSpace: "nowrap", overflow: "visible" }}>{T("Tu rango", "Your range")}</div>}
                {ticks.map(v => (
                  <div key={v} style={{ position: "absolute", bottom: 0, left: pos(v), transform: "translateX(-50%)", fontSize: 11, color: MUTED, fontVariantNumeric: "tabular-nums" }}>{v}K</div>
                ))}
              </div>
              {!isMobile && <div style={{ ...text.label, textAlign: "right" }}>{T("Rango", "Range")}</div>}
            </div>

            {/* Comparativa: una fila por sector y tamaño */}
            <div style={{ maxHeight: 480, overflowY: "auto", borderTop: `1px solid ${LINE}`, marginTop: 4 }}>
              {list.map(g => {
                const on = g.key === markedKey;
                const isOpen = open === g.key;
                const toggle = () => { setChosen(g.key); setOpen(isOpen ? null : g.key); };
                return (
                  <div key={g.key} style={{ borderBottom: `1px solid ${ROW_LINE}`, background: on ? SURFACE : "transparent", boxShadow: on ? `inset 3px 0 0 ${BRAND}` : "none", opacity: g.hasLevel ? 1 : 0.5 }}>
                    <div role="button" tabIndex={0} onClick={toggle} onKeyDown={e => { if (e.key === "Enter") toggle(); }}
                      title={T("Pulsa para marcarla como la más parecida y ver sus niveles", "Click to mark it as the closest and see its levels")}
                      style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr) auto" : cols, gap: isMobile ? "6px 10px" : 12, alignItems: "stretch", padding: "0 10px", cursor: "pointer", minHeight: 52 }}>
                      <div style={{ minWidth: 0, alignSelf: "center", padding: "8px 0" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {where(g) || g.role}{on && <span style={{ fontSize: 11, fontWeight: 700, color: BRAND, marginLeft: 6 }}>{T("Más parecido", "Closest")}</span>}
                        </div>
                        <div style={{ fontSize: 11, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sizeTxt(g)} · {g.role}</div>
                      </div>
                      {!isMobile && <div style={{ position: "relative", minWidth: 0 }}>{layers}{bar(g, on)}</div>}
                      <div style={{ textAlign: "right", alignSelf: "center", padding: "8px 0" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: INK, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{rangeTxt(g.lo, g.hi, g.open)}</div>
                        <div style={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>{levelsTxt(g)}</div>
                      </div>
                      {isMobile && <div style={{ gridColumn: "1 / -1", position: "relative", height: 24, marginBottom: 8 }}>{layers}{bar(g, on)}</div>}
                    </div>
                    {isOpen && (
                      <div style={{ padding: "2px 10px 12px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {g.bands.map(b => (
                          <span key={b.id || b.band} style={{ fontSize: 12, color: TEXT, background: WHITE, border: `1px solid ${LINE}`, borderRadius: 8, padding: "4px 8px" }}>
                            <span style={{ color: MUTED }}>{b.band || "—"} </span><strong style={{ color: INK }}>{bandTxt(b)}</strong>{b.bonus ? <span style={{ color: MUTED }}> · {b.bonus}</span> : null}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
              <span style={{ fontSize: 11, color: MUTED }}>{T("Pulsa una fila para marcarla como la más parecida y ver sus niveles.", "Click a row to mark it as the closest and see its levels.")}</span>
              {chosen && <button onClick={() => setChosen(null)} style={{ ...linkBtn, color: MUTED, fontWeight: 600 }}>{T("Volver a la sugerencia automática", "Back to the automatic suggestion")}</button>}
            </div>
          </div>
        )}
      </div>

      {/* Ofertas y datos de mercado: una fila por dato, sin la fuente */}
      <div style={{ padding: `${isMobile ? 14 : 18}px ${pad}px ${pad}px`, borderTop: `1px solid ${ROW_LINE}` }}>
        {sectionHead(T("Ofertas y datos de mercado", "Job ads and market data"),
          <label style={{ ...text.cardSub, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", minHeight: isMobile ? 40 : undefined }}>
            <input type="checkbox" checked={otherCur} onChange={e => setOtherCur(e.target.checked)} style={{ accentColor: BRAND, width: 15, height: 15, margin: 0 }} />
            {T("Incluir otras divisas", "Include other currencies")}
          </label>)}
        <div style={{ ...text.cardSub, marginTop: 6 }}>
          {T("Mismos nombres del puesto que la referencia. Miles, sin convertir de divisa. En gris, los de hace más de 12 meses.", "Same role names as the reference. Thousands, not converted between currencies. Greyed out: older than 12 months.")}
        </div>
        {obsErr && <div style={{ ...errorText, marginTop: 8 }}>{T("No se pudieron cargar: ", "Couldn't load them: ")}{obsErr}</div>}
        {obs === null && !obsErr && <div style={{ ...text.body, color: MUTED, marginTop: 12 }}>{T("Cargando…", "Loading…")}</div>}
        {obs && obs.length === 0 && !obsErr && (
          <div style={{ ...text.body, color: MUTED, marginTop: 12 }}>
            {otherCur
              ? T("No hay ofertas ni datos guardados para estos nombres del puesto. Prueba con otros en el buscador de arriba.", "No job ads or data saved for these role names. Try others in the search above.")
              : T("No hay ofertas ni datos en euros para estos nombres del puesto. Prueba con otros nombres arriba o marca «Incluir otras divisas».", "No job ads or data in euros for these role names. Try other names above or tick «Include other currencies».")}
          </div>
        )}
        {obs && obs.length > 0 && (
          <div style={{ marginTop: 10, maxHeight: 420, overflowY: "auto", borderTop: `1px solid ${LINE}` }}>
            {obs.map(o => {
              const d = o.observed_on ? new Date(`${o.observed_on}T00:00:00`) : null;
              const when = d ? d.toLocaleDateString(es ? "es-ES" : "en-GB", { month: "short", year: "numeric" }) : null;
              const old = d && Date.now() - d.getTime() > YEAR_MS;
              const eur = !o.currency || o.currency === "EUR";
              const sym = CUR_SYMBOL[o.currency] || o.currency || "";
              const lo = o.min_k != null ? Number(o.min_k) : null;
              const hi = o.max_k != null ? Number(o.max_k) : null;
              const nums = lo != null && hi != null && hi !== lo ? `${fmtK(lo)}–${fmtK(hi)}` : (lo ?? hi) != null ? `${fmtK(lo ?? hi)}` : null;
              const range = !nums ? "—" : eur ? `${nums}K €` : `${sym}${nums}K`;
              const extras = [o.bonus && "+ bonus", o.equity && "+ equity"].filter(Boolean).join(" ");
              const yrs = T("años", "yrs");
              const years = o.years_min != null && o.years_max != null ? `${o.years_min}-${o.years_max} ${yrs}`
                : o.years_min != null ? `+${o.years_min} ${yrs}` : o.years_max != null ? `≤${o.years_max} ${yrs}` : null;
              const kindKey = norm(o.source_kind).replace(/[^a-z]+/g, "_").replace(/^_|_$/g, "");
              const kind = SOURCE_KIND[kindKey] ? T(...SOURCE_KIND[kindKey]) : (o.source_kind || "");
              const modeKey = norm(o.work_mode).replace(/[^a-z]+/g, "");
              const mode = WORK_MODE[modeKey] ? T(...WORK_MODE[modeKey]) : o.work_mode;
              // Base de pago solo si no es la anual de siempre (p. ej. diaria)
              const basis = o.pay_basis && !/annual|anual|year|año/i.test(o.pay_basis) ? o.pay_basis : null;
              return (
                <div key={o.id} style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr) auto" : "64px minmax(0, 1fr) auto", gap: isMobile ? "2px 10px" : 12, alignItems: "center", padding: "10px", borderBottom: `1px solid ${ROW_LINE}`, opacity: old ? 0.45 : 1 }}>
                  {!isMobile && <div style={{ fontSize: 11, color: MUTED, fontVariantNumeric: "tabular-nums" }}>{when || "—"}</div>}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "normal" : "nowrap" }}>
                      {[o.role, o.seniority, years].filter(Boolean).join(" · ")}
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isMobile ? "normal" : "nowrap" }}>
                      {[isMobile && when, o.company_type, o.sector || o.area, o.city || o.country, mode].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: INK, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {range}{extras && <span style={{ fontWeight: 600, color: MUTED }}> {extras}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: MUTED, whiteSpace: "nowrap" }}>{[kind, basis].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {obs && obs.length > 0 && <div style={{ fontSize: 11, color: MUTED, marginTop: 8 }}>{T(`${obs.length} datos, del más reciente al más antiguo`, `${obs.length} data points, newest first`)}</div>}
      </div>

      {/* Rango recomendado: lo que se publica al cliente */}
      <div style={{ padding: `${isMobile ? 14 : 18}px ${pad}px ${pad}px`, borderTop: `1px solid ${ROW_LINE}` }}>
        {sectionHead(T("Rango recomendado", "Recommended range"),
          <span style={text.cardSub}>
            {reco ? T(`Publicado el ${new Date(reco.published_at).toLocaleDateString("es-ES")}: el cliente lo ve`, `Published ${new Date(reco.published_at).toLocaleDateString("en-GB")}: the client sees it`) : T("Sin publicar: el cliente no ve nada", "Not published: the client sees nothing")}
          </span>)}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "110px 110px minmax(0, 1fr)", gap: 10, marginTop: 10 }}>
          {field("min", T("Mín. (K)", "Min (K)"), "90")}
          {field("max", T("Máx. (K)", "Max (K)"), "110")}
          {field("bonus", "Bonus", T("15–20 %", "15–20 %"), isMobile ? { gridColumn: "1 / -1" } : null)}
        </div>
        <div style={{ marginTop: 10 }}>
          {field("perks", T("Equity y otros", "Equity & other perks"), T("p. ej. 0,5–1 % en stock options, seguro médico, coche", "e.g. 0.5–1% stock options, health insurance, car"))}
        </div>
        <label style={{ display: "block", marginTop: 10 }}>
          <span style={{ ...text.label, display: "block", marginBottom: 6 }}>{T("Tu lectura", "Your read")}</span>
          <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={3}
            placeholder={T("Por qué este rango, qué cambia si suben o bajan…", "Why this range, what changes if it goes up or down…")}
            style={{ ...input, height: "auto", padding: "10px 13px", lineHeight: 1.6, resize: "vertical", ...(isMobile ? { fontSize: 16 } : null) }} />
        </label>
        {recoText({ min_k: num(form.min), max_k: num(form.max), bonus: form.bonus }) && (
          <div style={{ ...text.bodySm, color: MUTED, marginTop: 8 }}>{T("El cliente verá: ", "The client will see: ")}<strong style={{ color: INK }}>{recoText({ min_k: num(form.min), max_k: num(form.max), bonus: form.bonus })}</strong>{form.perks.trim() && <> · {T("Equity y otros: ", "Equity & other: ")}<strong style={{ color: INK }}>{form.perks.trim()}</strong></>}</div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
          <button onClick={() => publish(true)} disabled={!canPublish} style={{ ...(canPublish ? btn.primary : btn.disabled), ...(isMobile ? { minHeight: 44, flex: 1 } : null) }}>
            {saving ? T("Guardando...", "Saving...") : reco ? T("Volver a publicar", "Publish again") : T("Publicar", "Publish")}
          </button>
          {reco && !askWithdraw && <button onClick={() => setAskWithdraw(true)} style={{ ...linkBtn, color: MUTED, fontWeight: 600, ...(isMobile ? { minHeight: 40 } : null) }}>{T("Retirar", "Withdraw")}</button>}
          {saved && <span style={savedText}>{T("✓ Guardado", "✓ Saved")}</span>}
        </div>
        {askWithdraw && (
          <ConfirmBar style={{ marginTop: 12 }} message={T("¿Retirar el rango? El cliente dejará de verlo.", "Withdraw the range? The client will no longer see it.")}
            actionLabel={T("Retirar", "Withdraw")} cancelLabel={T("Cancelar", "Cancel")} busy={saving}
            onConfirm={() => publish(null)} onCancel={() => setAskWithdraw(false)} />
        )}
      </div>
      </>}
    </div>
  );
};

export default SalaryReco;
